import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination,
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Cpu, Type, AlignLeft, Tag, Hash, Link, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetTechStack,
  adminGetTechStackItem,
  adminCreateTechStackItem,
  adminUpdateTechStackItem,
  adminDeleteTechStackItem,
} from "../../api/techStack";

// API-valid values
const CATEGORIES = [
  "FRONTEND", "BACKEND", "DATABASE", "DEVOPS",
  "CLOUD", "SECURITY", "TOOLS", "OTHER",
];

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];

const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const CATEGORY_STYLES = {
  FRONTEND: { bg: "rgba(37,99,235,0.08)",   color: "#2563EB" },
  BACKEND:  { bg: "rgba(139,92,246,0.08)",  color: "#7C3AED" },
  DATABASE: { bg: "rgba(6,182,212,0.08)",   color: "#0891B2" },
  DEVOPS:   { bg: "rgba(245,158,11,0.08)",  color: "#B45309" },
  CLOUD:    { bg: "rgba(34,197,94,0.08)",   color: "#16A34A" },
  SECURITY: { bg: "rgba(239,68,68,0.08)",   color: "#DC2626" },
  TOOLS:    { bg: "rgba(15,23,42,0.06)",    color: "#475569" },
  OTHER:    { bg: "rgba(15,23,42,0.06)",    color: "#475569" },
};

const DEFAULT_VALUES = {
  name: "",
  category: "OTHER",
  description: "",
  icon_url: "",
  order: 0,
  status: "DRAFT",
};

export default function TechStackPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Queries
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-tech-stack"],
    queryFn: () => adminGetTechStack().then(r => r.data),
  });

  // Fetch single item for edit
  const { data: editItemData, isLoading: isLoadingEdit } = useQuery({
    queryKey: ["admin-tech-stack-item", editing?.id],
    queryFn: () => adminGetTechStackItem(editing?.id).then(r => r.data),
    enabled: !!editing?.id && open,
    staleTime: 0,
  });

  // Update form when edit data loads
  useEffect(() => {
    if (editItemData && editing) {
      reset({
        name: editItemData.name ?? "",
        category: editItemData.category ?? "OTHER",
        description: editItemData.description ?? "",
        icon_url: editItemData.icon_url ?? "",
        order: editItemData.order ?? 0,
        status: editItemData.status ?? "DRAFT",
      });
    }
  }, [editItemData, editing, reset]);

  const createMutation = useMutation({
    mutationFn: adminCreateTechStackItem,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-tech-stack"]); 
      notify("Item added"); 
      closeDialog(); 
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't add item", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateTechStackItem(id, data),
    onSuccess: () => { 
      qc.invalidateQueries(["admin-tech-stack"]); 
      qc.invalidateQueries(["admin-tech-stack-item", editing?.id]);
      notify("Item updated"); 
      closeDialog(); 
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update item", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteTechStackItem,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-tech-stack"]); 
      notify("Item deleted"); 
      setConfirmDelete(null); 
    },
    onError: (err) => { 
      notify(err?.response?.data?.message || "Couldn't delete item", "error"); 
      setConfirmDelete(null); 
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // Form submit
  const onSubmit = (values) => {
    const payload = {
      name: values.name,
      category: values.category || "OTHER",
      description: values.description || "",
      icon_url: values.icon_url || "",
      order: values.order !== "" && values.order != null ? Number(values.order) : 0,
      status: values.status || "DRAFT",
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  // Dialog helpers
  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false); 
    setEditing(null); 
    reset(DEFAULT_VALUES);
  };

  // Search and pagination
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter items based on search query
  const filteredItems = items.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const startIndex = page * rowsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + rowsPerPage);

  return (
    <AdminShell title="Tech Stack">
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Technologies and tools shown on the public site. Set status to Active to make an item visible.
        </Typography>
        <Button
          variant="contained" 
          startIcon={<Plus size={18} />} 
          onClick={openCreate}
          sx={{
            borderRadius: "10px", 
            textTransform: "none", 
            fontWeight: 700, 
            bgcolor: "#2563EB", 
            boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", 
            flexShrink: 0,
            "&:hover": { bgcolor: "#1D4ED8" }
          }}
        >
          Add Item
        </Button>
      </Box>

      {/* Search bar */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search by name, category, or description..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{
              sx: { fontSize: 13.5 }
            }}
          />
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Item", "Category", "Order", "Status", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 180 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <Cpu size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      {searchQuery ? "No matching items found" : "No tech stack items yet"}
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search." : "Add technologies to display on the public site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedItems.map((item) => {
                const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.DRAFT;
                const categoryStyle = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.OTHER;
                return (
                  <TableRow key={item.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    {/* Item */}
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={item.icon_url || undefined}
                          sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: categoryStyle.bg, color: categoryStyle.color, flexShrink: 0 }}
                        >
                          <Cpu size={15} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Category */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={item.category || "OTHER"} 
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: categoryStyle.bg, color: categoryStyle.color }}
                      />
                    </TableCell>

                    {/* Order */}
                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {item.order ?? 0}
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={item.status || "DRAFT"} 
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small" 
                              onClick={() => openEdit(item)} 
                              disabled={saving}
                              sx={{
                                border: "1px solid rgba(15,23,42,0.1)", 
                                borderRadius: "8px", 
                                "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                              }}
                            >
                              <Pencil size={14} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small" 
                            onClick={() => setConfirmDelete(item)}
                            disabled={deleteMutation.isPending}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)", 
                              borderRadius: "8px", 
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        
        {/* Pagination */}
        {!isLoading && filteredItems.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": {
                pl: 2, pr: 1,
              },
              "& .MuiTablePagination-selectLabel": {
                fontSize: 13.5,
                color: "text.secondary",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: 13.5,
                color: "text.secondary",
              },
            }}
          />
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog 
        open={open} 
        onClose={closeDialog} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: "18px",
            overflow: "hidden" 
          } 
        }}
      >
        {/* Gradient header */}
        <Box sx={{ 
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          px: 3, 
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>
              {editing ? "Edit Item" : "New Tech Stack Item"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              {editing ? "Update this technology entry." : "Add a technology to your public tech stack."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
            {isLoadingEdit ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {/* Basic Information Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Type size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Basic Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Name is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Name"
                          fullWidth
                          disabled={saving}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || "e.g. React, Django, PostgreSQL"}
                          placeholder="e.g. React, Django, PostgreSQL"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Type size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Category"
                          fullWidth
                          disabled={saving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Tag size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        >
                          {CATEGORIES.map(c => (
                            <MenuItem key={c} value={c}>
                              <Chip label={c} size="small" sx={{ fontWeight: 600, fontSize: 12, height: 20, bgcolor: CATEGORY_STYLES[c]?.bg || "rgba(107,114,128,0.1)", color: CATEGORY_STYLES[c]?.color || "#6B7280" }} />
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          fullWidth
                          multiline
                          rows={2}
                          disabled={saving}
                          placeholder="Brief description of this technology"
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}><AlignLeft size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                  </Box>
                </Card>

                {/* Display Settings Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Cpu size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Display Settings
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="icon_url"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Icon URL"
                          fullWidth
                          disabled={saving}
                          placeholder="https://cdn.example.com/icons/react.svg"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Link size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="order"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Display Order"
                          fullWidth
                          disabled={saving}
                          type="number"
                          placeholder="0"
                          inputProps={{ min: 0, step: 1 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Hash size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Status"
                          fullWidth
                          disabled={saving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Tag size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        >
                          {STATUSES.map(s => (
                            <MenuItem key={s} value={s}>
                              <Chip label={s} size="small" sx={{ fontWeight: 600, fontSize: 12, height: 20, bgcolor: STATUS_STYLES[s]?.bg || "rgba(107,114,128,0.1)", color: STATUS_STYLES[s]?.color || "#6B7280" }} />
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>
                </Card>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit" 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{ 
                textTransform: "none", 
                fontWeight: 700, 
                borderRadius: "10px", 
                bgcolor: "#2563EB", 
                px: 2.5, 
                minWidth: 100,
                "&:hover": { bgcolor: "#1D4ED8" }
              }}
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => !deleteMutation.isPending && setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this item?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.name}" will be removed permanently. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending} 
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ 
              textTransform: "none", 
              fontWeight: 700, 
              borderRadius: "10px", 
              bgcolor: "#EF4444", 
              px: 2.5, 
              minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}